/**
 * บัญชีเข้าระบบแอพของโครงการ (อีเมล + รหัสผ่าน) ในฟอร์มโครงการ — PO 30 ก.ย. 2026
 *
 * เดิมทีม Support เก็บในชีต "บัญชีเข้าระบบ KODER3" แยกจาก KRP · 1 โครงการ 1 บัญชี
 * - ข้อมูลมาจาก endpoint แยก /sites/{id}/app-credentials (รหัสผ่านไม่มากับข้อมูลโครงการ)
 * - เห็นรหัสจริง/คัดลอก/ตั้ง/ล้าง ต้องมี sites.app_credentials (backend ลง log ทุกครั้งที่ดู) · ไม่มี = เห็นแค่อีเมล
 * - ฟอร์มไม่เคยใส่รหัสเดิมกลับลงช่อง: เว้นว่าง = ไม่เปลี่ยน · ติ๊ก "ล้างรหัสผ่าน" = ลบ
 * - บันทึกต่อจากการบันทึกโครงการ: ฟอร์มเรียก commit(siteId) หลังได้ id (โครงการใหม่ต้องสร้างก่อน)
 * - ช่องรหัสผ่าน autoComplete="new-password" — ไม่งั้นเบราว์เซอร์เติมรหัสผ่าน KRP ของผู้ใช้เองลงไป
 */
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RiEyeLine, RiEyeOffLine, RiFileCopyLine, RiKeyLine } from 'react-icons/ri';
import siteService, { SiteAppCredentialsUpdate } from '../../services/siteService';
import { showAlert } from '../../utils/alert';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

export interface SiteAppCredentialsHandle {
  /** บันทึกเฉพาะที่ผู้ใช้แก้ · ไม่มีอะไรเปลี่ยน/ไม่มีสิทธิ์ = ไม่ยิง · error → throw ข้อความไทย */
  commit: (siteId: number) => Promise<void>;
  /** ตรวจก่อนบันทึกโครงการ — คืนข้อความ error หรือ null */
  validate: () => string | null;
}

interface Props {
  siteId?: number;
  mode: 'create' | 'edit' | 'view';
  /** hasPermission('sites.app_credentials') */
  canManage: boolean;
  /** หน้าดูห่อด้วย DetailSection ที่มีหัวข้อแล้ว — ซ่อนหัวข้อของตัวเองไม่ให้ซ้ำ 2 ชั้น (E2E R1 29 ต.ค. 2026) */
  hideHeader?: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const queryKey = (id?: number) => ['sites', id, 'app-credentials'];

const SiteAppCredentialsSection = forwardRef<SiteAppCredentialsHandle, Props>(({ siteId, mode, canManage, hideHeader = false }, ref) => {
  const queryClient = useQueryClient();
  const { data: meta, isLoading } = useQuery({
    queryKey: queryKey(siteId),
    queryFn: () => siteService.getAppCredentials(siteId as number),
    enabled: !!siteId,
    staleTime: 0,
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [showTyped, setShowTyped] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // เปิดโครงการใหม่/อีกโครงการ → ล้างค่าที่พิมพ์ค้าง + รหัสที่เปิดดูไว้
  useEffect(() => {
    setEmail(meta?.app_login_email ?? '');
    setPassword('');
    setClearPassword(false);
    setRevealed(null);
    setShowTyped(false);
  }, [siteId, meta?.app_login_email]);

  const originalEmail = meta?.app_login_email ?? '';
  const hasPassword = !!meta?.has_password;
  const editable = mode !== 'view' && canManage;

  const validate = () => {
    if (!editable) return null;
    const e = email.trim();
    if (e && !EMAIL_RE.test(e)) return 'อีเมลเข้าระบบแอพไม่ถูกต้อง';
    return null;
  };

  useImperativeHandle(ref, () => ({
    validate,
    commit: async (id: number) => {
      if (!editable) return;
      const payload: SiteAppCredentialsUpdate = {};
      if (email.trim() !== originalEmail) payload.app_login_email = email.trim() || null;
      if (password) payload.password = password;
      else if (clearPassword && hasPassword) payload.clear_password = true;
      if (Object.keys(payload).length === 0) return;
      try {
        await siteService.updateAppCredentials(id, payload);
        await queryClient.invalidateQueries({ queryKey: queryKey(id) });
      } catch (error) {
        throw new Error(getApiErrorMessage(error, 'บันทึกบัญชีเข้าระบบแอพไม่สำเร็จ'));
      }
    },
  }));

  const reveal = async (): Promise<string | null> => {
    if (!siteId) return null;
    if (revealed !== null) return revealed;
    setBusy(true);
    try {
      const r = await siteService.revealAppCredentials(siteId);
      setRevealed(r.password ?? '');
      return r.password ?? '';
    } catch (error) {
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ดูรหัสผ่านไม่สำเร็จ'), 'error');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    const pw = await reveal();
    if (!pw) return;
    try {
      await navigator.clipboard.writeText(pw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showAlert('คัดลอกไม่ได้', 'เบราว์เซอร์ไม่อนุญาตให้คัดลอก — กดแสดงรหัสแล้วคัดลอกเอง', 'warning');
    }
  };

  const passwordDisplay = (
    <div className="flex items-center gap-2 min-h-[44px]" data-testid="site-app-password-display">
      <span className="font-mono text-sm text-secondary-900">
        {!hasPassword ? <span className="text-secondary-600 font-sans">ยังไม่มีรหัสผ่าน</span> : revealed ?? '●●●●●●●●'}
      </span>
      {hasPassword && canManage && (
        <>
          <button
            type="button"
            onClick={() => (revealed !== null ? setRevealed(null) : reveal())}
            disabled={busy}
            className="inline-flex items-center justify-center h-9 w-9 rounded-md text-secondary-600 hover:text-primary-700 hover:bg-secondary-100"
            aria-label={revealed !== null ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
            title={revealed !== null ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน (ระบบบันทึกว่าใครดู)'}
          >
            {revealed !== null ? <RiEyeOffLine className="h-4 w-4" /> : <RiEyeLine className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={copy}
            disabled={busy}
            className="inline-flex items-center justify-center h-9 w-9 rounded-md text-secondary-600 hover:text-primary-700 hover:bg-secondary-100"
            aria-label="คัดลอกรหัสผ่าน"
            title="คัดลอกรหัสผ่าน"
          >
            <RiFileCopyLine className="h-4 w-4" />
          </button>
          {copied && <span className="text-xs text-success-700" role="status">คัดลอกแล้ว</span>}
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="site-app-credentials">
      {!hideHeader && (
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
          <div className="p-1.5 bg-primary-100 rounded-lg">
            <RiKeyLine className="h-4 w-4 text-primary-700" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">บัญชีเข้าระบบแอพ</h3>
        </div>
      )}
      {siteId && isLoading ? (
        <p className="text-sm text-secondary-600">กำลังโหลด...</p>
      ) : editable ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="site-app-email" className="block text-sm font-medium text-secondary-700 mb-1">อีเมล</label>
            <input
              id="site-app-email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="เช่น kvisit5@koder3.com"
              autoComplete="off"
              maxLength={255}
            />
          </div>
          <div>
            <label htmlFor="site-app-password" className="block text-sm font-medium text-secondary-700 mb-1">รหัสผ่าน</label>
            <div className="relative">
              <input
                id="site-app-password"
                type={showTyped ? 'text' : 'password'}
                className="input pr-11"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (e.target.value) setClearPassword(false); }}
                placeholder={hasPassword ? 'มีรหัสผ่านแล้ว — เว้นว่าง = ไม่เปลี่ยน' : 'รหัสผ่าน'}
                autoComplete="new-password"
                maxLength={200}
              />
              <button
                type="button"
                onClick={() => setShowTyped(v => !v)}
                className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-9 w-9 rounded-md text-secondary-600 hover:bg-secondary-100"
                aria-label={showTyped ? 'ซ่อนรหัสที่พิมพ์' : 'แสดงรหัสที่พิมพ์'}
              >
                {showTyped ? <RiEyeOffLine className="h-4 w-4" /> : <RiEyeLine className="h-4 w-4" />}
              </button>
            </div>
            {hasPassword && (
              <>
                <div className="mt-1 text-xs text-secondary-600">รหัสปัจจุบัน:</div>
                {passwordDisplay}
                <label className="mt-1 inline-flex items-center gap-2 text-sm text-secondary-700 min-h-[44px] cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-secondary-300"
                    checked={clearPassword}
                    disabled={!!password}
                    onChange={(e) => setClearPassword(e.target.checked)}
                  />
                  ล้างรหัสผ่าน
                </label>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="block text-sm font-medium text-secondary-700 mb-1">อีเมล</div>
            <div className="text-sm text-secondary-900 min-h-[44px] flex items-center break-all" data-testid="site-app-email-display">
              {originalEmail || <span className="text-secondary-600">-</span>}
            </div>
          </div>
          <div>
            <div className="block text-sm font-medium text-secondary-700 mb-1">รหัสผ่าน</div>
            {mode === 'create' ? <span className="text-sm text-secondary-600">-</span> : passwordDisplay}
            {!canManage && hasPassword && (
              <p className="text-xs text-secondary-600">ต้องมีสิทธิ์ "ดู/ตั้งบัญชีเข้าระบบแอพ" จึงจะดูรหัสผ่านได้</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

SiteAppCredentialsSection.displayName = 'SiteAppCredentialsSection';
export default SiteAppCredentialsSection;
